import React, { useState, useEffect } from 'react';
import { 
  Box, 
  SimpleGrid, 
  Flex, 
  Text, 
  Spinner, 
  Center, 
  Button,
  VStack, 
  Icon,
  useToast
} from '@chakra-ui/react';
import { FiAlertTriangle, FiRefreshCw, FiBarChart2, FiTrendingUp, FiDollarSign, FiActivity, FiPieChart } from 'react-icons/fi';
import { useDashboardData } from '../hooks/useApi';
import PositionsList from '../components/PositionsList';
import ActivityFeed from '../components/ActivityFeed';
import { formatCurrency, formatPercent, getValueColor } from '../utils/formatters';

// Import necessary CSS
import '../styles/positions.css';
import '../styles/real-time-updates.css';

const Dashboard = () => {
  // Generate a unique key to force a complete refresh when needed
  const [componentKey, setComponentKey] = useState(Date.now());
  const toast = useToast();
  
  // Get consolidated dashboard data
  const { 
    summary, 
    positions, 
    stats,
    activities,
    isLoading, 
    isError, 
    error, 
    refetch,
    rawData
  } = useDashboardData();
  
  // Track refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Handle manual refresh with toast notifications
  const handleRefresh = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      await refetch();
      setComponentKey(Date.now());
    } catch (error) {
   
      
      toast({
        title: "Refresh failed",
        description: error?.message || "An error occurred",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Set up auto refresh every 15 seconds
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      // Only refresh if not already refreshing
      if (!isRefreshing) {
        refetch().catch(err => {
         
        });
      }
    }, 15000); // 15 seconds
    
    return () => clearInterval(refreshInterval);
  }, [refetch, isRefreshing]);
  
  // Loading state
  if (isLoading && !summary) {
    return (
      <Center h="calc(100vh - 120px)">
        <VStack spacing={4}>
          <Spinner size="xl" color="green.400" thickness="4px" />
          <Text color="gray.500">Loading dashboard data...</Text>
        </VStack>
      </Center>
    );
  }
  
  // Error state
  if (isError && !summary) {
    return (
      <Center h="calc(100vh - 120px)">
        <VStack spacing={4} textAlign="center">
          <Icon as={FiAlertTriangle} boxSize={12} color="red.400" />
          <Text fontSize="xl" fontWeight="bold" color="red.400">
            Unable to Load Dashboard
          </Text>
          <Text color="gray.500" maxW="md">
            {error?.message || "There was an error loading your dashboard data."}
          </Text>
          <Button colorScheme="red" onClick={handleRefresh} leftIcon={<FiRefreshCw />}>
            Retry
          </Button>
        </VStack>
      </Center>
    );
  }
  
  return (
    <Box 
      display="flex"
      flexDirection="column"
      position="relative"
      h={{ base: "auto", md: "calc(100vh - 84px)" }}
      pb={{ base: 4, md: 0 }}
    >
      {/* Top cards with data */}
    {/* Top cards with data */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
        {/* Portfolio Card */}
        <Box className="crypto-card p-4 group">
          <Flex items="center" justify="space-between">
            <Box flex="1">
              <Flex alignItems="baseline" gap={1} mb={2}>
                <Text fontSize="2xl" fontWeight="semibold" color="white">
                  {!isLoading && summary?.totalBalance ? 
                    `$${parseFloat(summary.totalBalance).toFixed(2)}` : (
                    <span className="skeleton skeleton-line w-1/2"></span>
                  )}
                </Text>
                <Text fontSize="xs" color="gray.400">Portfolio Value</Text>
              </Flex>
              <Text fontSize="sm" color="gray.300">
                {!isLoading && summary?.availableBalance ? 
                  `Available: $${parseFloat(summary.availableBalance).toFixed(2)}` : (
                  <span className="skeleton skeleton-line w-2/3"></span>
                )}
              </Text>
            </Box>
            <Box 
              className="crypto-icon w-11 h-11" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              opacity="0.8"
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.3s"
              bgGradient="linear(135deg, #6366f1, #4f46e5)"
            >
              <Icon as={FiDollarSign} w={5} h={5} color="white" />
            </Box>
          </Flex>
        </Box>
        
        {/* Open Positions Card */}
        <Box className="crypto-card p-4 group">
          <Flex items="center" justify="space-between">
            <Box flex="1">
              <Flex alignItems="baseline" gap={1} mb={2}>
                <Text fontSize="2xl" fontWeight="semibold" color="white">
                  {!isLoading && stats ? 
                    stats.totalOpen : (
                    <span className="skeleton skeleton-line w-1/3"></span>
                  )}
                </Text>
                <Text fontSize="xs" color="gray.400">Open Positions</Text>
              </Flex>
              <Text fontSize="sm">
                {!isLoading && stats ? (
                  <>
                    <Text as="span" color="green.400" mr={3}>
                      Long: {stats.totalLong}
                    </Text>
                    <Text as="span" color="red.400">
                      Short: {stats.totalShort}
                    </Text>
                  </>
                ) : (
                  <span className="skeleton skeleton-line w-2/3"></span>
                )}
              </Text>
            </Box>
            <Box 
              className="crypto-icon w-11 h-11" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              opacity="0.8"
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.3s"
              bgGradient="linear(135deg, #10b981, #059669)"
            >
              <Icon as={FiBarChart2} w={5} h={5} color="white" />
            </Box>
          </Flex>
        </Box>
        
        {/* PnL Card */}
        <Box className="crypto-card p-4 group">
          <Flex justify="space-between" align="flex-start">
            <Box flex="1">
              <Flex alignItems="baseline" gap={1} mb={2}>
                <Text fontSize="xs" color="gray.400">Unrealized PnL</Text>
              </Flex>
              <Text 
                fontSize="2xl" 
                fontWeight="semibold" 
                color={!isLoading && summary?.unrealizedPnl ? getValueColor(summary.unrealizedPnl) : "white"}
                mb={1}
              >
                {!isLoading && summary?.unrealizedPnl !== undefined ? 
                  formatCurrency(summary.unrealizedPnl) : (
                  <span className="skeleton skeleton-line w-1/2"></span>
                )}
              </Text>
              <Text fontSize="sm" color="gray.300">
                {!isLoading && summary?.marginUsed !== undefined ? 
                  `Margin used: ${formatCurrency(summary.marginUsed)}` : (
                  <span className="skeleton skeleton-line w-2/3"></span>
                )}
              </Text>
            </Box>
            
            <Box 
              className="crypto-icon w-11 h-11" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              opacity="0.8"
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.3s"
              bgGradient="linear(135deg, #8b5cf6, #6d28d9)"
            >
              <Icon as={FiTrendingUp} w={5} h={5} color="white" />
            </Box>
          </Flex>
        </Box>
        
        {/* Total PnL Card */}
        <Box className="crypto-card p-4 group">
          <Flex justify="space-between" align="flex-start">
            <Box flex="1">
              <Flex alignItems="baseline" gap={1} mb={2}>
                <Text fontSize="xs" color="gray.400">Total PnL</Text>
              </Flex>
              <Text 
                fontSize="2xl" 
                fontWeight="semibold" 
                color={!isLoading ? 
                  (positions && positions.length > 0 ? 
                    getValueColor(positions.reduce((sum, pos) => sum + (parseFloat(pos.pnlPercent) || 0), 0)) : 
                    "white") : 
                  "white"
                }
                mb={1}
              >
                {!isLoading ? 
                  (positions && positions.length > 0 ? 
                    formatPercent(positions.reduce((sum, pos) => sum + (parseFloat(pos.pnlPercent) || 0), 0)) : 
                    "0.00%") : (
                  <span className="skeleton skeleton-line w-1/2"></span>
                )}
              </Text>
              <Text fontSize="sm" color="gray.300">
                {!isLoading ? "Portfolio Performance" : (
                  <span className="skeleton skeleton-line w-2/3"></span>
                )}
              </Text>
            </Box>
            
            <Box 
              className="crypto-icon w-11 h-11" 
              display="flex" 
              alignItems="center" 
              justifyContent="center"
              opacity="0.8"
              _groupHover={{ opacity: 1 }}
              transition="opacity 0.3s"
              bgGradient="linear(135deg, #f59e0b, #d97706)"
            >
              <Icon as={FiPieChart} w={5} h={5} color="white" />
            </Box>
          </Flex>
        </Box>
      </SimpleGrid>
      
      {/* Main content area with positions and activity feed */}
      <Box
        display="flex" 
        flexDirection={{ base: "column", lg: "row" }}
        gap={4}
        flex="1"
        h={{ base: "auto", lg: "calc(100vh - 220px)" }}
      >
        {/* Enhanced Positions panel */}
        <Box 
          className="crypto-cardz" 
          p={{ base: 3, lg: 4 }}
          display="flex" 
          flexDirection="column"
          flex={{ lg: "2" }}
          h={{ base: "500px", lg: "100%" }}
          w={{ base: "100%", lg: "auto" }}
          minH={{ base: "500px" }}
          overflow="hidden"
        >
          {/* The enhanced PositionsList component */}
          <PositionsList
            positions={positions || []}
            summary={summary}
            onRefresh={handleRefresh}
            isLoading={isLoading}
            isError={isError}
            error={error}
            key={`positions-component-${componentKey}`}
          />
        </Box>
        
        {/* Activity feed */}
        <Box
          className="crypto-card"
          p={{ base: 3, lg: 4 }}
          display="flex"
          flexDirection="column"
          flex={{ lg: "1" }}
          h={{ base: "500px", lg: "100%" }}
          w={{ base: "100%", lg: "auto" }}
          minH={{ base: "500px" }}
          overflow="hidden"
        >
          <Flex mb={3} flexShrink={0} justifyContent="space-between" alignItems="center">
            <Box>
              <Text fontSize={{ base: "sm", lg: "md" }} fontWeight="semibold" color="white">
                <Icon as={FiActivity} boxSize="4" color="green.400" /> AI Activity
              </Text>
              <Text fontSize={{ base: "2xs", lg: "xs" }} color="gray.400">
                Market insights from Wells AI
              </Text>
            </Box>
            
            {/* AI Status Indicator */}
            <Flex className="ai-status">
              <Box className="ai-status-dot"></Box>
              <Text>Monitoring</Text>
            </Flex>
          </Flex>
          
          {/* Activity Feed Component */}
          <Box flex="1" overflow="hidden">
            <ActivityFeed
              activities={activities || []}
              isLoading={isLoading}
              isFullHeight={true}
            />
          </Box>
        </Box>
      </Box>
      
      {/* Credentials notice */}
      {summary && (!summary.apiConfig) && (
        <Box mt={4} p={3} bg="rgba(255, 165, 0, 0.1)" borderRadius="lg" border="1px solid" borderColor="orange.400">
          <Text fontSize="sm" fontWeight="bold" color="orange.400" mb={1}>
            🔧 Setup Required
          </Text>
          <Text fontSize="xs" color="gray.300">
            To enable enhanced position features, please configure your Bitget API credentials in the{' '}
            <Text as="span" color="orange.400" fontWeight="bold">API Settings</Text> page.
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;